import cn from 'classnames';
import React, { InputHTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';
import Button from '../button';
import { ArrowNextIcon } from '@/components/icons/arrow-next';

export interface Props extends InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  inputClassName?: string;
  buttonClassName?: string;
  label?: string;
  name: string;
  error?: string;
  type?: string;
  shadow?: boolean;
  variant?: 'normal' | 'solid' | 'outline' | 'line' | 'complementary';
  dimension?: 'small' | 'medium' | 'big';
  disabledButton?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  buttonType?: 'submit' | 'button';
  onButtonClick?: () => void;
  buttonAriaLabel?: string;
}

const variantClasses = {
  normal:
    'bg-gray-100 border border-border-base focus:shadow focus:bg-light focus:border-accent',
  solid:
    'bg-gray-100 border border-border-100 focus:bg-light focus:border-accent',
  complementary:
    'bg-gray-100 border border-border-100 border-accent border-r-transparent rounded-tr-none rounded-br-none focus:bg-light focus:border-r-transparent',
  outline: 'border border-border-base focus:border-accent',
  line: 'ltr:pl-0 rtl:pr-0 border-b border-border-base rounded-none focus:border-accent',
};

const sizeClasses = {
  small: 'text-sm h-9',
  medium: 'h-12',
  big: 'h-14',
};

const InputButton = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      label,
      name,
      error,
      children,
      variant = 'normal',
      dimension = 'medium',
      shadow = false,
      disabled = false,
      type = 'text',
      inputClassName,
      buttonClassName,
      disabledButton = false,
      buttonType = 'submit',
      onButtonClick = () => {},
      icon = <ArrowNextIcon className="w-5" />,
      loading,
      buttonAriaLabel = 'Enviar',
      ...rest
    },
    ref,
  ) => {
    return (
      <div className={twMerge(cn('flex flex-col', className))}>
        <div className={twMerge(cn('flex', className))}>
          <input
            id={name}
            name={name}
            type={type}
            ref={ref}
            className={twMerge(
              cn(
                'flex w-full appearance-none items-center rounded-3xl px-4 text-sm text-heading transition duration-300 ease-in-out focus:outline-0 focus:ring-0',
                shadow && 'focus:shadow',
                variantClasses[variant],
                sizeClasses[dimension],
                disabled && 'cursor-not-allowed bg-gray-100',
                inputClassName,
              ),
            )}
            disabled={disabled}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            aria-invalid={error ? 'true' : 'false'}
            {...rest}
          />
          <Button
            type={buttonType}
            aria-label={buttonAriaLabel}
            className={twMerge(
              cn(
                'w-4-12 text-sm tracking-[0.2px] rounded-tl-none rounded-tr-[30px] rounded-br-[30px] rounded-bl-none border-accent border-l-transparent',
                buttonClassName,
              ),
            )}
            loading={loading}
            disabled={disabledButton}
            onClick={onButtonClick}
          >
            {icon}
          </Button>
        </div>
        {error && <p className="pt-2 text-xs text-red-500 flex-1">{error}</p>}
      </div>
    );
  },
);
InputButton.displayName = 'InputButton';
export default InputButton;
